import {Button, Dialog, DialogActions, DialogTitle} from "@mui/material";
import {useContext, useState} from "react";
import useWindowSize from "react-use/lib/useWindowSize";
import {PopperZIndexProvider} from "../../../../misc/scroller/PopperZIndexProvider";
import {CheckboxCol, RenderStringCol, StringCol, Table, useTableRefreshAll, ViewOnly} from "../../../../misc/scroller/Table";
import {api} from "../../../../api/API";
import {forcesByOthers, prefixedName, ProjectSupplierCategory} from "../../../../api/SupplierCategories";
import {useProjectId} from "../ProjectName";
import {useAlternative} from "../../quote/alternative/Alternative";
import {ChangePreviewContext} from "../ShopDrawingChangePreview";
import {blue} from "@mui/material/colors";
import {useSupplierCategories} from "./SupplierContext";
import {useSnackbar} from "../../../../misc/Snackbar";

const tableName = "project.supplier-categories.manage";

export function ManageProjectSuppliers(props: {
    onDone: () => void;
}) {
    const win = useWindowSize();
    const project = useProjectId();
    const alternative = useAlternative();
    const previewChangeset = useContext(ChangePreviewContext);
    const previewId = previewChangeset.enabled ? previewChangeset.sessionId : undefined;
    // openings tables resolve each row's by-others from the supplier by-us setting, so a
    // change here must reload them. Deferred to close so the reload happens once.
    const suppliers = useSupplierCategories();
    const refreshAll = useTableRefreshAll();
    const snack = useSnackbar();
    const [changed, setChanged] = useState(false);

    // reload the openings/hardware tables (and the supplier context they inherit from)
    // once, when the dialog closes, if anything changed.
    const close = () => {
        if (changed) {
            suppliers.reload();
            refreshAll();
        }
        props.onDone();
    };

    return (
        <Dialog open onClose={close}>
            <DialogTitle>Edit Supplied Categories</DialogTitle>

            <div style={{
                minHeight: win.height * 0.5,
                minWidth: 400,
                display: "flex",
                flexDirection: "column",
                position: "relative",
            }}>
                <PopperZIndexProvider value={1400}>
                    <Table<ProjectSupplierCategory>
                        name={tableName}
                        fetch={() => api.supplierCategories.listForProject({project, alternative, previewChangeset: previewId})
                            .then(rows => rows.map(row => forcesByOthers(row) ? {...row, byUs: false} : row))}
                        fetchDeps={[project, alternative, previewId]}
                        cellCustomize={{
                            backgroundColor: (row) => {
                                if(row.backup && row.backup.byUs !== row.byUs) {
                                    return blue["100"];
                                }
                            }
                        }}
                        columns={[
                            ViewOnly(RenderStringCol<ProjectSupplierCategory>("Name", (row) => prefixedName(row), 200)),
                            CheckboxCol<ProjectSupplierCategory>("By Us", "byUs", 100, (row) => forcesByOthers(row)),
                            StringCol<ProjectSupplierCategory>("Note", "note", 250)
                        ]}
                        onChange={async (row) => {
                            // recalc runs as a background task the api polls for, so it can
                            // take a while; show a loading snack once it exceeds 250ms. The
                            // table's own success/error snack replaces it when we settle.
                            const loadingTimer = setTimeout(() => snack.loading("Updating supplied category..."), 250);
                            try {
                                const updated = await api.supplierCategories.upsertForProject(row);
                                setChanged(true);
                                return updated;
                            } finally {
                                clearTimeout(loadingTimer);
                            }
                        }}
                    />
                </PopperZIndexProvider>
            </div>

            <DialogActions>
                <Button onClick={close}>Close</Button>
            </DialogActions>
        </Dialog>
    )
}
